import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/useSeo";
import { Eyebrow } from "@/components/ui-kit";
import { SEO } from "@/data/seo";

const EFFECTIVE = "July 1, 2026";

const SECTIONS = [
  {
    id: "who",
    title: "Who we are",
    body: [
      "Project Management Network (\"PMN\", \"we\") is a fractional program leadership firm based in Dallas–Fort Worth, Texas. This policy explains what information we collect when you visit pmnetwork.org, how we use it, and the choices you have.",
      "Questions about this policy or your data: brandon@pmnetwork.org.",
    ],
  },
  {
    id: "forms",
    title: "Information you send us",
    body: [
      "When you submit the \"Discuss an Initiative\" form we collect your name, work email, company, and the description of your initiative or delivery challenge. We use this solely to respond to your inquiry and to evaluate whether PMN is a fit for the work.",
      "Submissions are stored in PMN's private lead database and a notification is emailed to brandon@pmnetwork.org through our transactional email provider. We do not sell, rent, or share this information with third parties for their own marketing. We retain inquiries for as long as needed to follow up and for our business records; you may ask us to delete yours at any time.",
    ],
  },
  {
    id: "scheduling",
    title: "Scheduling (Calendly)",
    body: [
      "If you book a call, scheduling is handled by Calendly. Any details you enter there (name, email, meeting notes) are collected by Calendly under its own privacy policy and shared with PMN so we can hold the meeting. See calendly.com/privacy.",
    ],
  },
  {
    id: "analytics",
    title: "Analytics and cookies",
    body: [
      "We may use Google Analytics 4 to understand how visitors use the site — pages viewed, buttons clicked (such as \"Discuss an Initiative\"), form completions, scheduling clicks, and how far pages are scrolled. Google Analytics sets first-party cookies (for example _ga and _ga_*) to distinguish visits. IP addresses are anonymized. Google's use of this data is described at policies.google.com/technologies/partner-sites. You can opt out with the Google Analytics browser add-on or by blocking cookies in your browser.",
      "The hosting platform that serves this site (Emergent) also loads its own analytics script, which may record page interactions, including session replays, for platform monitoring. PMN does not receive or control that data. If you prefer not to be recorded, browser tracking-protection tools or script blockers will prevent it.",
      "We do not use advertising cookies, retargeting pixels, or cross-site tracking.",
    ],
  },
  {
    id: "technical",
    title: "Technical data",
    body: [
      "Like most websites, our servers and hosting provider automatically log basic technical information such as IP address, browser type, referring page, and timestamps. Images and fonts are served from content-delivery networks (Unsplash and our own host), which receive your IP address in order to deliver those files.",
    ],
  },
  {
    id: "rights",
    title: "Your choices and rights",
    body: [
      "You can ask us to access, correct, or delete information you have submitted by emailing brandon@pmnetwork.org. We will respond within a reasonable period. Texas and other U.S. state privacy laws may give you additional rights; we honor those requests regardless of where you live.",
      "This site is intended for business audiences and is not directed to children under 16. We do not knowingly collect information from children.",
    ],
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: [
      `We may update this policy as the site or our tools change. The effective date at the top will be revised when we do. Effective: ${EFFECTIVE}.`,
    ],
  },
];

export default function Privacy() {
  useSeo({ ...SEO["/privacy"], path: "/privacy" });

  return (
    <>
      <section className="bg-charcoal pt-36 pb-16 text-paper lg:pt-44 lg:pb-24">
        <div className="mx-auto max-w-8xl px-6 lg:px-10">
          <Eyebrow dark>Legal</Eyebrow>
          <h1 className="mt-7 max-w-3xl font-serif text-4xl font-normal leading-[1.06] tracking-tightish sm:text-5xl lg:text-[4rem]" data-testid="privacy-title">
            Privacy policy.
          </h1>
          <p className="mt-7 max-w-xl text-base leading-[1.8] text-paper/70">
            Plain language about what we collect, why, and the choices you have. Effective {EFFECTIVE}.
          </p>
        </div>
      </section>

      <section className="bg-paper py-20 lg:py-28">
        <div className="mx-auto grid max-w-8xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <nav aria-label="On this page" className="lg:col-span-3">
            <p className="text-xs uppercase tracking-mega text-teal-deep">Contents</p>
            <ul className="mt-6 space-y-3 text-sm text-charcoal/70">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="link-underline hover:text-teal" data-testid={`privacy-toc-${s.id}`}>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-14 lg:col-span-8 lg:col-start-5" data-testid="privacy-content">
            {SECTIONS.map((s) => (
              <article key={s.id} id={s.id} className="scroll-mt-32">
                <h2 className="font-serif text-2xl tracking-tightish text-charcoal sm:text-3xl">{s.title}</h2>
                {s.body.map((p, i) => (
                  <p key={i} className="mt-5 max-w-2xl text-base leading-[1.85] text-charcoal/75">
                    {p}
                  </p>
                ))}
              </article>
            ))}
            <p className="border-t border-charcoal/10 pt-8 text-sm text-charcoal/60">
              Ready to talk?{" "}
              <Link to="/contact" className="text-teal link-underline" data-testid="privacy-contact-link">
                Discuss an Initiative
              </Link>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
